import { useMemo } from 'react';
import { cn } from '../../lib/cn.ts';
import { markdown } from './markdown.ts';

/*
 * Wundo writes the same reply for every channel, so what arrives here is the
 * markdown Teams and Slack render too. Rendered rather than shown raw, or the
 * drawer displays its asterisks.
 */

export interface ChatMarkdownProps {
  children: string;
  className?: string;
}

/**
 * One message's text.
 *
 * Block markdown, not inline only: a reply that lists three things should read
 * as three things.
 */
export function ChatMarkdown({ children, className }: ChatMarkdownProps) {
  const html = useMemo(() => markdown.render(children), [children]);

  return (
    <div
      data-slot='chat-markdown'
      className={cn(
        'text-md leading-relaxed',
        '[&_p]:my-0 [&_p+p]:mt-2',
        '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-4.5',
        '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-4.5',
        '[&_li+li]:mt-1',
        '[&_strong]:font-bold',
        '[&_em]:italic',
        '[&_a]:underline [&_a]:underline-offset-2',
        '[&_code]:rounded-xs [&_code]:bg-fill [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-sm',
        /* One step above the body, which is `text-md`: a heading the same size as what it heads is not one. */
        '[&_h1]:text-lg [&_h1]:font-bold [&_h2]:text-lg [&_h2]:font-bold [&_h3]:font-bold',
        /* A heading opens a section: air above it, a little under it, none at the very top. */
        '[&_h1]:mt-4 [&_h2]:mt-4 [&_h3]:mt-3 [&_h1]:mb-1.5 [&_h2]:mb-1.5 [&_h3]:mb-1 [&>:first-child]:mt-0',
        '[&_li.task-list-item]:-ml-4.5 [&_li.task-list-item]:list-none',
        '[&_li.task-list-item_input]:mr-1.5 [&_li.task-list-item_input]:size-3.25',
        '[&_li.task-list-item_input]:accent-primary [&_li.task-list-item_input]:align-middle',
        '[&_li.task-list-item[data-checked=true]]:text-ink-muted [&_li.task-list-item[data-checked=true]]:line-through',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
